<template>
  <div
    v-if="modelValue && user"
    class="fixed inset-0 z-[1050] bg-black/40 flex items-end md:items-stretch md:justify-end"
    @click.self="$emit('update:modelValue', false)"
  >
    <aside class="bg-white w-full md:w-[420px] max-h-[92vh] md:max-h-none rounded-t-lg md:rounded-none overflow-hidden flex flex-col text-left">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div class="min-w-0">
          <h2 class="text-lg font-semibold text-gray-900 truncate">Profile</h2>
          <p class="text-xs text-gray-500 truncate">{{ user.university?.name || 'Student profile' }}</p>
        </div>
        <button
          type="button"
          class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
          @click="$emit('update:modelValue', false)"
          aria-label="Close profile"
        >
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-5 space-y-5">
        <section class="text-center">
          <peer-avatar :user="user" size="xl" class="mx-auto" />
          <h3 class="mt-3 text-xl font-semibold text-gray-900">{{ user.name || 'Student' }}</h3>
          <p class="text-sm text-gray-500">{{ yearText }}</p>
        </section>

        <section class="grid grid-cols-1 gap-3">
          <div v-if="user.university?.name" class="rounded-lg bg-gray-50 p-3">
            <p class="text-xs font-medium text-gray-500">University</p>
            <p class="text-sm text-gray-900">{{ user.university.name }}</p>
          </div>
          <div v-if="showFaculty && user.university?.faculty" class="rounded-lg bg-gray-50 p-3">
            <p class="text-xs font-medium text-gray-500">Faculty</p>
            <p class="text-sm text-gray-900">{{ user.university.faculty }}</p>
          </div>
          <div v-if="showCourseName && user.university?.courseName" class="rounded-lg bg-gray-50 p-3">
            <p class="text-xs font-medium text-gray-500">Course</p>
            <p class="text-sm text-gray-900">{{ user.university.courseName }}</p>
          </div>
          <div v-if="showLevelOfStudy && user.university?.levelOfStudy" class="rounded-lg bg-gray-50 p-3">
            <p class="text-xs font-medium text-gray-500">Level</p>
            <p class="text-sm text-gray-900">{{ user.university.levelOfStudy }}</p>
          </div>
          <div v-if="showCampus && user.university?.campuses?.length" class="rounded-lg bg-gray-50 p-3">
            <p class="text-xs font-medium text-gray-500">Campus</p>
            <p class="text-sm text-gray-900">{{ user.university.campuses.join(', ') }}</p>
          </div>
        </section>

        <section v-if="user.university?.modules?.length">
          <h4 class="font-semibold text-gray-900 mb-2">Modules</h4>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="module in user.university.modules"
              :key="module"
              class="px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs"
            >
              {{ module }}
            </span>
          </div>
        </section>
      </div>

      <div class="p-4 border-t border-gray-100 grid grid-cols-2 gap-2">
        <button
          type="button"
          class="px-3 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
          :class="connectionButtonClass"
          @click="$emit('connect', user.id)"
        >
          <span class="material-symbols-outlined text-base">{{ connectionIcon }}</span>
          {{ connectionButtonText }}
        </button>
        <button
          type="button"
          :disabled="connectionStatus !== 'connected'"
          class="px-3 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          @click="$emit('chat', user.id)"
        >
          <span class="material-symbols-outlined text-base">chat</span>
          Chat
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import PeerAvatar from './PeerAvatar.vue';

export default {
  name: 'PeerProfileDrawer',
  components: { PeerAvatar },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    },
    connectionStatus: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'connect', 'chat'],
  computed: {
    yearText() {
      const year = this.user?.university?.studyYear;
      if (!year) return 'Student';
      const yearNum = parseInt(year);
      if (Number.isNaN(yearNum)) return year;
      const suffixes = ['st', 'nd', 'rd'];
      const suffix = yearNum <= 3 ? suffixes[yearNum - 1] : 'th';
      return `${yearNum}${suffix} Year Student`;
    },
    showFaculty() {
      return this.user?.privacySettings?.showFaculty !== false;
    },
    showCourseName() {
      return this.user?.privacySettings?.showCourseName !== false;
    },
    showLevelOfStudy() {
      return this.user?.privacySettings?.showLevelOfStudy !== false;
    },
    showCampus() {
      return this.user?.privacySettings?.showCampus !== false;
    },
    connectionButtonText() {
      switch (this.connectionStatus) {
        case 'connected': return 'Connected';
        case 'pending': return 'Pending';
        case 'request': return 'Accept';
        default: return 'Connect';
      }
    },
    connectionIcon() {
      switch (this.connectionStatus) {
        case 'connected': return 'how_to_reg';
        case 'pending': return 'pending';
        case 'request': return 'person_add';
        default: return 'person_add';
      }
    },
    connectionButtonClass() {
      switch (this.connectionStatus) {
        case 'connected': return 'bg-green-100 text-green-700 hover:bg-green-200';
        case 'pending': return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
        case 'request': return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
        default: return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
      }
    }
  }
};
</script>
